import { Avatar, Heading, Text, Tooltip, VStack } from '@chakra-ui/react';
import {
  PerAgentOfferAcceptRateFragment,
  PerAgentTransactionStatsFragment,
  PerOfficeOfferAcceptRateFragment,
  PerOfficeTransactionStatsFragment,
} from '@client/graphql/__generated__/types';
import EventEmitter from 'eventemitter3';
import { FC, memo } from 'react';
import {
  GridTable,
  GridTableBody,
  GridTableCell,
  GridTableHead,
  GridTableHeadItem,
  GridTableRow,
} from '~/services/document/common/GridTable/GridTable';
import { Null } from '~/services/document/utils/graphql';
import { classify } from '~/services/document/utils/string';
import { usePerformanceList } from '../hooks/usePerformanceLIst';

interface PerformanceListProps {
  type: 'agent' | 'office';
  transactionStats?:
    | PerAgentTransactionStatsFragment[]
    | PerOfficeTransactionStatsFragment[];
  offerAcceptRate?:
    | PerAgentOfferAcceptRateFragment[]
    | PerOfficeOfferAcceptRateFragment[];
  scrollElement?: HTMLDivElement | null;
}

export const PerformanceList: FC<PerformanceListProps> = memo(
  function PerformanceList(props) {
    const { type, transactionStats, offerAcceptRate, scrollElement } = props;

    const {
      items,
      observeRow,
      visibleEventEmitter,
      onOrderBy,
      orderBy,
      baseColumns,
      visibleStatsColumns,
    } = usePerformanceList({
      offerAcceptRate,
      transactionStats,
      scrollElement,
    });

    // Calculate total number of columns
    const totalColumns = baseColumns.length + visibleStatsColumns.length;

    return (
      <VStack alignItems="stretch" spacing={4} width="100%">
        <Heading as="h2" fontSize="xl" px={4}>
          {type === 'agent' ? 'Agents' : 'Offices/Teams'}
        </Heading>

        <GridTable
          gridTemplateColumns={`${
            type === 'agent' ? '150px' : '200px'
          } ${' auto'.repeat(totalColumns - 1)}`}
          mx={4}
          orderBy={orderBy}
          onOrderBy={onOrderBy}
        >
          <GridTableHead
            backdropFilter="blur(50px)"
            backgroundColor="card.bg"
            borderBottom="none"
            borderColor="card.border"
            borderRadius="12px 12px 0 0"
            borderStyle="solid"
            borderWidth="1px 1px 0 1px"
            boxShadow="0px 0px 1px 0px var(--chakra-colors-card-boxShadow) inset;"
            color="whiteAlpha.600"
            position="sticky"
            textAlign="center"
            textShadow="1px 1px 1px var(--chakra-colors-blackAlpha-400)"
            top={{ base: '-1px', md: '115px' }}
            zIndex={2}
          >
            {/* Base columns */}
            {baseColumns.map((column) => (
              <GridTableHeadItem
                key={column.key}
                orderByKey={column.orderByKey}
                textAlign={column.key === 'name' ? 'left' : 'center'}
              >
                {column.title}
              </GridTableHeadItem>
            ))}

            {/* Grouped average_stats columns */}
            {visibleStatsColumns.map((column) => (
              <GridTableHeadItem
                key={column.key}
                orderByKey={column.orderByKey}
              >
                {column.title}
              </GridTableHeadItem>
            ))}
          </GridTableHead>

          <GridTableBody
            backgroundColor="card.bg"
            borderColor="card.border"
            borderRadius="0 0 12px 12px"
            borderStyle="solid"
            borderWidth="1px"
            boxShadow="0px 0px 1px 0px var(--chakra-colors-card-boxShadow) inset;"
          >
            {items?.map((item, index) => (
              <PerformanceListRow
                key={index}
                baseColumns={baseColumns}
                index={index}
                item={item}
                observeRow={observeRow}
                type={type}
                visibleEventEmitter={visibleEventEmitter}
                visibleStatsColumns={visibleStatsColumns}
              />
            ))}
          </GridTableBody>
        </GridTable>
      </VStack>
    );
  }
);

const PerformanceListRow: FC<{
  item: Partial<
    Omit<PerAgentTransactionStatsFragment, '__typename'> &
      Omit<PerAgentOfferAcceptRateFragment, '__typename'>
  >;
  index: number;
  observeRow: (node: HTMLDivElement) => void;
  visibleEventEmitter: EventEmitter;
  baseColumns: Array<{
    key: string;
    title: string;
    orderByKey?: string;
    formatter?: (value: number | null) => string;
  }>;
  visibleStatsColumns: Array<{
    key: string;
    title: string;
    orderByKey?: string;
    formatter?: (value: number | null) => string;
  }>;
  type: 'agent' | 'office';
}> = memo(function PerformanceListRow(props) {
  const {
    item,
    index,
    observeRow,
    visibleEventEmitter,
    baseColumns,
    visibleStatsColumns,
    type,
  } = props;

  return (
    <GridTableRow
      key={item.name}
      _hover={{ bgColor: 'whiteAlpha.200' }}
      fontSize="md"
      height="50px"
      index={index}
      innerRef={observeRow}
      visibleEventEmitter={visibleEventEmitter}
    >
      {/* Base columns */}
      {baseColumns.map((column) => (
        <GridTableCell
          key={column.key}
          maxWidth={
            column.key === 'name'
              ? type === 'agent'
                ? '150px'
                : '200px'
              : undefined
          }
        >
          {column.key === 'name' ? (
            <Tooltip label={item.name}>
              <Text
                overflow="hidden"
                textAlign="left"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                <Avatar mr={2} name={classify(item.name || '')} size="xs" />
                {item.name}
              </Text>
            </Tooltip>
          ) : column.formatter ? (
            column.formatter(item[column.key] as number | null)
          ) : (
            item[column.key]
          )}
        </GridTableCell>
      ))}

      {/* Grouped columns */}
      {visibleStatsColumns.map((column) => (
        <GridTableCell key={column.key}>
          {column.formatter
            ? column.formatter(item[column.key] as number | null)
            : item[column.key] === Null
              ? 'N/A'
              : item[column.key]}
        </GridTableCell>
      ))}
    </GridTableRow>
  );
});
